import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";

interface TocItem {
  id: string;
  title: string;
  icon: React.ReactNode;
}

interface TableOfContentsProps {
  items: TocItem[];
  isMobile?: boolean;
}

export const TableOfContents = ({ items, isMobile = false }: TableOfContentsProps) => {
  const [activeId, setActiveId] = useState<string>(() => {
    const hash = window.location.hash;
    return hash ? hash.replace('#', '') : '';
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );

    items.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [items]);

  // Handle hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        setActiveId(hash.replace('#', ''));
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <nav className={cn("space-y-1", isMobile && "max-h-[60vh] overflow-y-auto")}>
      {items.map((item) => (
        <button
          key={item.id}
          onClick={() => handleClick(item.id)}
          className={cn(
            "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all",
            "hover:bg-accent/50 group",
            activeId === item.id
              ? "bg-primary/10 text-primary font-medium border-l-2 border-primary"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <span className={cn(
            "transition-colors",
            activeId === item.id ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
          )}>
            {item.icon}
          </span>
          <span className="flex-1 text-sm">{item.title}</span>
          {activeId === item.id && (
            <ChevronRight className="w-4 h-4 text-primary" />
          )}
        </button>
      ))}
    </nav>
  );
};
